import { object as zodObject, url as zodUrl } from 'zod/mini';

import { API_URL_SUMMARY_LAMBDAS, EPIC_2ND_LOGIN_FLOW_ENABLED } from '../config/Environment';
import {
	EPIC_CLIENT_AUD,
	EPIC_CLIENT_AUTH_URL,
	EPIC_CLIENT_LAUNCH_ISS,
	EPIC_CLIENT_TOKEN_ENDPOINT,
} from '../pages/epic/consts';
import { EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_NAME } from '../react-router/routes/PatientPortraitRoutes/PatientPortraitRoutes.utils';
import { isCurrentScreenLayoutEmbeddedOrOverlay } from '../utilities/screenLayout';
import {
	TOKEN_EXP_TIME,
	SMART_ON_FHIR_LUNCH_TIME,
	AUTH_0_TOKEN,
	AUTH_0_CONNECTION_NAME,
	REDIRECTING_TO_EPIC_TIME,
	NEXT_URL_QUERY_PARAMS,
	REDIRECTING_TO_AUTH0_TIME,
} from '../utils/consts';
import {
	getEncodedStateWithEmbeddedLaunchDestination,
	getEmbeddedLaunchDestinationFromUrlParamsOrNull,
} from './embeddedLaunchDestination';

interface EpicClientEnv {
	readonly legacy?: string;
	readonly current: string;
}

interface EpicClientJson {
	readonly production: EpicClientEnv;
	readonly staging: EpicClientEnv;
	readonly dev: EpicClientEnv;
}

interface EpicSmartConfiguration {
	readonly authorizationEndpoint: string;
	readonly tokenEndpoint: string;
	readonly aud: string;
}

const _EPIC_SMART_LAUNCH_URL_QUERY_PARAM_KEYS = [
	'aud',
	'clientId',
	'connection',
	'environment',
	'idTokenHint',
	'iss',
	'launch',
	'nextUrl',
	'redirectUri',
	'responseType',
	'scope',
	'state',
] as const satisfies ReadonlyArray<string>;

const EPIC_SMART_LAUNCH_URL_QUERY_PARAM = {
	aud: 'aud',
	clientId: 'client_id',
	connection: 'connection',
	environment: 'environment',
	idTokenHint: 'id_token_hint',
	iss: 'iss',
	launch: 'launch',
	nextUrl: 'nextUrl',
	redirectUri: 'redirect_uri',
	responseType: 'response_type',
	scope: 'scope',
	state: 'state',
} as const satisfies Readonly<Record<(typeof _EPIC_SMART_LAUNCH_URL_QUERY_PARAM_KEYS)[number], string>>;

const EPIC_SMART_WELLKNOWN_CONFIGURATION_SCHEMA = zodObject({
	authorization_endpoint: zodUrl(),
	token_endpoint: zodUrl(),
});

const LEGACY_EPIC_CONNECTION_NAME_LIST = new Set(['nems', 'nems-staging', 'noms-staging', 'navina-fhir-connection']);

const EPIC_CLIENT_JSON = {
	production: {
		legacy: '4cd5b80a-f393-4e52-b292-8cb7173149e7',
		current: '912d1aea-3a76-483f-a334-e7b1e645fcf5',
	},
	staging: {
		legacy: 'abfa3dbd-37bd-4f03-b121-6426ebe37039',
		current: '89805fd1-b57a-4c40-a940-44f687e179b9',
	},
	dev: {
		current: '6633d4ef-fd34-499a-853a-9d0302eaf6f0',
	},
} as const satisfies EpicClientJson;

const AUTH0_BASE_URL = 'https://navina.us.auth0.com';

const AUTH0_LOGIN_AUTHORIZATION_URL = `${AUTH0_BASE_URL}/authorize`; // https://navina.us.auth0.com/authorize

const CRYPTO_RANDOM_UINT32_COUNT = 1;
const UINT32_TO_UNIT_INTERVAL_DIVISOR = 0xffffffff + 1;

const EPIC_INITIAL_LOAD_DEBUG_LOG_LEVEL = 5;

const MILLISECONDS_PER_SECOND = 1_000;
const TOKEN_EXPIRY_RENEWAL_BUFFER_MS = 60 * 60 * MILLISECONDS_PER_SECOND;
const TOKEN_EXPIRATION_SECONDS_WHEN_MISSING = 0;

function getNonSecuredRandomInt(min: number, max: number): number {
	const range = max - min + 1;
	return globalThis.Math.floor(globalThis.Math.random() * range) + min;
}

function getSecuredRandomInt(min: number, max: number): number {
	try {
		const range = max - min + 1;
		const randomArrays = new globalThis.Uint32Array(CRYPTO_RANDOM_UINT32_COUNT);

		globalThis.crypto.getRandomValues(randomArrays);

		const [randomValue] = randomArrays;
		if (randomValue === undefined) {
			throw new Error('Failed to generate a random value.');
		}

		const randomNumber = randomValue / UINT32_TO_UNIT_INTERVAL_DIVISOR;
		return globalThis.Math.floor(randomNumber * range) + min;
	} catch {
		return getNonSecuredRandomInt(min, max);
	}
}

function tryLoggingEpicDebugInfo(): void {
	try {
		const logRequestHeaders = new Headers({
			'content-type': 'application/json',
		});

		const logRequestBody = JSON.stringify({
			message: 'Auth0 EPIC initial load',
			extras: {
				url: globalThis.location.href,
			},
			logLevel: EPIC_INITIAL_LOAD_DEBUG_LOG_LEVEL,
		});

		const logRequestOptions = {
			method: 'POST',
			headers: logRequestHeaders,
			body: logRequestBody,
		};

		const logEndpointUrl = new URL('/log', API_URL_SUMMARY_LAMBDAS).toString();
		fetch(logEndpointUrl, logRequestOptions);
	} catch {}
}

function saveCurrentTimeToLocalStorage(key: string): void {
	localStorage.setItem(key, Date.now().toString());
}

function showErrorAlert(context: string, error: unknown): void {
	const message =
		// eslint-disable-next-line @typescript-eslint/no-base-to-string -- Fallback when all else fails
		`Authentication error during ${context}: ${error instanceof Error ? error.message : String(error)}` as const;
	console.error(message, error);
	// eslint-disable-next-line no-alert -- We need to show the error to the user
	globalThis.alert(message);
}

const RANDOM_NUMBER_FOR_STATE_MIN = 10_000_000;
const RANDOM_NUMBER_FOR_STATE_MAX = 99_999_999;
function createRandomNumberForState(): ReturnType<typeof getSecuredRandomInt> {
	return getSecuredRandomInt(RANDOM_NUMBER_FOR_STATE_MIN, RANDOM_NUMBER_FOR_STATE_MAX);
}

function createCommonUrlParams(): URLSearchParams {
	const redirectSearchParams = new URLSearchParams();
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.scope, 'launch openid fhirUser');
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.responseType, 'code');
	return redirectSearchParams;
}

function createStateWithLaunchDestination(urlParams: URLSearchParams): string {
	const launchDestination = getEmbeddedLaunchDestinationFromUrlParamsOrNull(urlParams);
	return getEncodedStateWithEmbeddedLaunchDestination(String(createRandomNumberForState()), launchDestination);
}

function getDefaultEpicRedirectUri(): string {
	const { pathname, origin } = globalThis.location;
	const isEmbeddedOrOverlay = isCurrentScreenLayoutEmbeddedOrOverlay();
	return isEmbeddedOrOverlay
		? `${origin}${pathname}`
		: `${origin}/${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_NAME}${pathname}`;
}

function createEpicUrlParams(urlParams: URLSearchParams, epicClientID: string): URLSearchParams {
	const redirectSearchParams = createCommonUrlParams();

	const aud = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.iss);
	const launch = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.launch);

	// @ts-expect-error - Legacy code, nullability not yet enforced
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.aud, aud);
	// @ts-expect-error - Legacy code, nullability not yet enforced
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.launch, launch);
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.clientId, epicClientID);
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.state, createStateWithLaunchDestination(urlParams));

	const redirectUrl = getDefaultEpicRedirectUri();

	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.redirectUri, redirectUrl);

	return redirectSearchParams;
}

/**
 * The Auth0 hop intentionally does NOT honor the launch URL's `redirect_uri`.
 * `auth.ts → handleAuthData` discriminates Auth0 callbacks from Epic
 * callbacks by checking whether the pathname contains `epic` — Epic paths are
 * skipped so the Epic-specific handler can claim the code. If we let Auth0
 * redirect back to (e.g.) `/embedded/epic/login`, the Auth0 code is stranded
 * because no handler claims it. The `launchDestination` still survives the
 * Auth0 hop because we round-trip it via the OAuth2 `state` parameter.
 */
function getAuth0RedirectUri(): string {
	return `${globalThis.location.origin}/${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_NAME}/login`;
}

function createAuth0UrlParams(urlParams: URLSearchParams): URLSearchParams {
	const redirectSearchParams = createCommonUrlParams();

	const iss = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.iss);
	const launch = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.launch);
	const clientId = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.clientId);
	const connection = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.connection);

	// @ts-expect-error - Legacy code, nullability not yet enforced
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.idTokenHint, launch);
	// @ts-expect-error - Legacy code, nullability not yet enforced
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.clientId, clientId);
	// @ts-expect-error - Legacy code, nullability not yet enforced
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.connection, connection);
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.redirectUri, getAuth0RedirectUri());
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.state, createStateWithLaunchDestination(urlParams));
	// @ts-expect-error - Legacy code, nullability not yet enforced
	redirectSearchParams.set(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.iss, iss);

	return redirectSearchParams;
}

function getEpicClientId(connectionName: string, windowLocation: Location): string {
	const allowedEnvs = ['staging', 'production'];

	const searchParams = new URLSearchParams(windowLocation.search);
	const envParam = searchParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.environment);

	const validatedEnvParam = envParam && allowedEnvs.includes(envParam) ? envParam : null;
	const envByOriginURL = windowLocation.origin.includes('app.navina.ai') ? 'production' : 'staging';
	const env = validatedEnvParam || envByOriginURL;

	const isLegacy = LEGACY_EPIC_CONNECTION_NAME_LIST.has(connectionName.toLowerCase());
	const isDev = connectionName === 'navina-fhir-connection-dev';

	// @ts-expect-error - Legacy code
	const epicClientId = EPIC_CLIENT_JSON[isDev ? 'dev' : env][isLegacy ? 'legacy' : 'current'];

	return epicClientId;
}

function getEpicCurrentClientAuthConfiguration() {
	const epicTokenEndpoint = localStorage.getItem(EPIC_CLIENT_TOKEN_ENDPOINT);
	const epicAud = localStorage.getItem(EPIC_CLIENT_AUD);
	const epicAuthUrl = localStorage.getItem(EPIC_CLIENT_AUTH_URL);

	return { epicAuthUrl, epicTokenEndpoint, epicAud };
}

function setEpicCurrentClientAuthConfiguration(epicAuthUrl: string, epicTokenEndpoint: string, epicAud: string): void {
	localStorage.setItem(EPIC_CLIENT_TOKEN_ENDPOINT, epicTokenEndpoint);
	localStorage.setItem(EPIC_CLIENT_AUD, epicAud);
	localStorage.setItem(EPIC_CLIENT_AUTH_URL, epicAuthUrl);
}

function getEpicClientSmartConfiguration(iss: string | null): Promise<EpicSmartConfiguration> {
	const { epicAuthUrl, epicTokenEndpoint, epicAud } = getEpicCurrentClientAuthConfiguration();

	if (epicTokenEndpoint && epicAud && epicAuthUrl) {
		// if configuration is already stored in local storage skip getting it again
		return Promise.resolve({
			authorizationEndpoint: epicAuthUrl,
			tokenEndpoint: epicTokenEndpoint,
			aud: epicAud,
		});
	}

	return fetch(`${iss}/.well-known/smart-configuration`).then(
		(wellKnownUrlResponse): Promise<EpicSmartConfiguration> => {
			return wellKnownUrlResponse.text().then((wellKnownConfiguration): EpicSmartConfiguration => {
				const wellknownConfigurationJson = EPIC_SMART_WELLKNOWN_CONFIGURATION_SCHEMA.parse(
					JSON.parse(wellKnownConfiguration),
				);
				const { authorization_endpoint: resolvedAuthUrl, token_endpoint: tokenEndpoint } = wellknownConfigurationJson;

				// @ts-expect-error - Legacy code, nullability not yet enforced
				setEpicCurrentClientAuthConfiguration(resolvedAuthUrl, tokenEndpoint, iss);

				return {
					authorizationEndpoint: resolvedAuthUrl,
					tokenEndpoint,
					// @ts-expect-error - Legacy code, nullability not yet enforced
					aud: iss,
				};
			});
		},
	);
}

function getTokenExpirationTime() {
	const expirationTimeInSecondsString = localStorage.getItem(TOKEN_EXP_TIME);
	const expirationTimeInSeconds = expirationTimeInSecondsString
		? Number(expirationTimeInSecondsString)
		: TOKEN_EXPIRATION_SECONDS_WHEN_MISSING;
	return expirationTimeInSeconds * MILLISECONDS_PER_SECOND - TOKEN_EXPIRY_RENEWAL_BUFFER_MS;
}

function redirectToOAuth2AuthorizationEndpoint(): void {
	if (!globalThis.location.href.toLowerCase().includes('epic/login')) {
		// First login
		return;
	}

	tryLoggingEpicDebugInfo();

	const urlParams = new URLSearchParams(globalThis.location.search);
	const launchCode = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.launch);

	if (!launchCode) {
		return;
	}

	saveCurrentTimeToLocalStorage(SMART_ON_FHIR_LUNCH_TIME);
	const auth0Token = localStorage.getItem(AUTH_0_TOKEN);
	const auth0ConnectionName = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.connection);
	const iss = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.iss);

	console.log('Epic login flow started with params:', {
		launchCode,
		iss,
		connection: auth0ConnectionName,
	});

	if (!iss) {
		showErrorAlert('parameter validation', 'Missing required URL parameter: iss');
		// Consider to throw new Error('Missing required URL parameter: iss');
	}

	if (!auth0ConnectionName) {
		showErrorAlert('parameter validation', 'Missing required URL parameter: connection');
		// Consider to throw new Error('Missing required URL parameter: connection');
	}

	// @ts-expect-error - Legacy code, nullability not yet enforced
	localStorage.setItem(AUTH_0_CONNECTION_NAME, auth0ConnectionName);
	// @ts-expect-error - Legacy code, nullability not yet enforced
	localStorage.setItem(EPIC_CLIENT_LAUNCH_ISS, iss);

	if (EPIC_2ND_LOGIN_FLOW_ENABLED && auth0Token && Date.now() < getTokenExpirationTime()) {
		// Second login
		console.log('Valid auth0Token found, proceeding with Epic redirect flow');

		getEpicClientSmartConfiguration(iss)
			.then((wellKnownConfiguration) => {
				// @ts-expect-error - Legacy code, nullability not yet enforced
				const epicClientID = getEpicClientId(auth0ConnectionName.toLowerCase(), globalThis.location);
				console.log('Epic client ID resolved:', epicClientID);

				const redirectSearchParams = createEpicUrlParams(urlParams, epicClientID);
				console.log('Epic redirect params:', redirectSearchParams.toString());

				const redirectUrl = `${wellKnownConfiguration.authorizationEndpoint}?${redirectSearchParams.toString()}`;

				console.log('Epic redirecting to the following URL:', redirectUrl);

				saveCurrentTimeToLocalStorage(REDIRECTING_TO_EPIC_TIME);
				globalThis.location.href = redirectUrl;
			})
			.catch((error) => {
				showErrorAlert('Epic SMART configuration fetch', error);
			});
	} else {
		const reasonToProceedWithAuth0RedirectFlow = !auth0Token
			? 'No auth0Token found'
			: !EPIC_2ND_LOGIN_FLOW_ENABLED && Date.now() < getTokenExpirationTime()
				? 'Epic 2nd login flow disabled'
				: 'auth0Token expired';
		console.log('Proceeding with Auth0 redirect flow. Reason:', reasonToProceedWithAuth0RedirectFlow);

		const nextUrlQueryParams = urlParams.get(EPIC_SMART_LAUNCH_URL_QUERY_PARAM.nextUrl);
		if (nextUrlQueryParams) {
			localStorage.setItem(NEXT_URL_QUERY_PARAMS, nextUrlQueryParams);
		}

		const redirectSearchParams = createAuth0UrlParams(urlParams);
		console.log('Auth0 redirect params:', redirectSearchParams.toString());

		// for auth0 connection to send the params in the correct way to the auth server
		const redirectUrl = `${AUTH0_LOGIN_AUTHORIZATION_URL}?${redirectSearchParams.toString()}`;

		console.log('Auth0 redirecting to the following URL:', redirectUrl);

		saveCurrentTimeToLocalStorage(REDIRECTING_TO_AUTH0_TIME);
		globalThis.location.href = redirectUrl;
	}
}

redirectToOAuth2AuthorizationEndpoint();
